'use client';

import { Box, Button, Field, NativeSelect, Text, VStack } from '@chakra-ui/react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';

export type TaskSlackDeliveryInput = {
  installationId: string;
  channelId: string;
  audienceHash: string;
  externalSharingConfirmed?: boolean;
  channelName?: string;
  external?: boolean;
};
type Channel = {
  id: string;
  name: string;
  private: boolean;
  external: boolean;
  audienceHash: string;
};
type Destinations = {
  installations: Array<{ id: string; name: string }>;
  channels: Channel[];
  nextCursor: string;
};

export function SlackTaskDeliveryField({
  value,
  onChange,
  load,
}: {
  value: TaskSlackDeliveryInput | null;
  onChange: (value: TaskSlackDeliveryInput | null) => void;
  load: (input: Record<string, unknown>) => Promise<Destinations>;
}) {
  const [installations, setInstallations] = useState<Destinations['installations']>([]);
  const [page, setPage] = useState<{
    key: string;
    channels: Channel[];
    cursor: string;
    busy: boolean;
    error: string;
  } | null>(null);
  const request = useRef<{ active: boolean } | null>(null);
  const fetchDestinations = useEffectEvent(load);
  const installationId = value?.installationId || '';
  const enabled = value !== null;
  const visiblePage = page?.key === installationId ? page : null;
  const channels = visiblePage?.channels || [];
  const cursor = visiblePage?.cursor || '';
  const busy = enabled && (!visiblePage || visiblePage.busy);
  const error = visiblePage?.error || '';
  const changeValue = (next: TaskSlackDeliveryInput | null) => {
    if (next?.installationId !== value?.installationId || (next === null) !== (value === null))
      setPage(null);
    onChange(next);
  };
  useEffect(() => {
    if (!enabled) return;
    const generation = { active: true };
    request.current = generation;
    void fetchDestinations({ installationId })
      .then(result => {
        if (!generation.active) return;
        setInstallations(result.installations);
        setPage({
          key: installationId,
          channels: result.channels,
          cursor: result.nextCursor,
          busy: false,
          error: '',
        });
      })
      .catch(reason => {
        if (generation.active)
          setPage({
            key: installationId,
            channels: [],
            cursor: '',
            busy: false,
            error: reason instanceof Error ? reason.message : 'Could not load Slack channels.',
          });
      });
    return () => {
      generation.active = false;
    };
  }, [enabled, installationId]);
  const loadMore = async() => {
    const generation = request.current;
    setPage(current => (current ? { ...current, busy: true, error: '' } : null));
    try {
      const result = await load({ installationId, cursor });
      if (!generation?.active) return;
      setPage(current => ({
        key: installationId,
        channels: [
          ...new Map(
            [...(current?.channels || []), ...result.channels].map(channel => [channel.id, channel]),
          ).values(),
        ],
        cursor: result.nextCursor,
        busy: false,
        error: '',
      }));
    } catch (reason) {
      if (generation?.active)
        setPage(current =>
          current
            ? {
              ...current,
              busy: false,
              error: reason instanceof Error ? reason.message : 'Could not load Slack channels.',
            }
            : null,
        );
    }
  };
  return (
    <VStack align="stretch" gap="3" borderTopWidth="1px" borderColor="ui.border.subtle" pt="4">
      <Box as="label" display="flex" alignItems="center" gap="2" fontSize="sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={event =>
            changeValue(
              event.target.checked ? { installationId: '', channelId: '', audienceHash: '' } : null,
            )
          }
        />
        Send results to Slack
      </Box>
      {value && (
        <>
          <Text fontSize="xs" color="ui.text.secondary">
            Send an executive summary and output links when the task finishes or needs attention.
            Replies start a conversation about the published result.
          </Text>
          <Field.Root>
            <Field.Label>Slack workspace</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={installationId}
                onChange={event =>
                  changeValue({
                    installationId: event.target.value,
                    channelId: '',
                    audienceHash: '',
                  })
                }
              >
                <option value="">Choose workspace</option>
                {installationId && !installations.some(item => item.id === installationId) && (
                  <option value={installationId}>Current workspace</option>
                )}
                {installations.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          {installationId && (
            <Field.Root>
              <Field.Label>Channel</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  value={value.channelId}
                  onChange={event => {
                    const channel = channels.find(item => item.id === event.target.value);
                    changeValue({
                      installationId,
                      channelId: channel?.id || '',
                      audienceHash: channel?.audienceHash || '',
                      channelName: channel?.name,
                      external: channel?.external,
                      externalSharingConfirmed: false,
                    });
                  }}
                >
                  <option value="">Choose channel</option>
                  {value.channelId && !channels.some(item => item.id === value.channelId) && (
                    <option value={value.channelId}>
                      #{value.channelName || value.channelId} (current)
                    </option>
                  )}
                  {channels.map(channel => (
                    <option key={channel.id} value={channel.id}>
                      #{channel.name}
                      {channel.private ? ' · Private' : ''}
                      {channel.external ? ' · External · Slack Connect' : ''}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.HelperText>
                Only channels where you and Attune are members appear.
              </Field.HelperText>
            </Field.Root>
          )}
          {busy && <Text fontSize="xs">Loading Slack channels…</Text>}
          {!busy && !error && installations.length === 0 && (
            <Text fontSize="xs">
              Link your Slack account in workspace integrations, then reopen task settings.
            </Text>
          )}
          {cursor && (
            <Button size="xs" variant="ghost" loading={busy} onClick={() => void loadMore()}>
              Load more channels
            </Button>
          )}
          {value.external && (
            <Box
              as="label"
              display="flex"
              gap="2"
              alignItems="start"
              fontSize="sm"
              color="ui.text.primary"
            >
              <input
                type="checkbox"
                checked={value.externalSharingConfirmed === true}
                onChange={event =>
                  changeValue({ ...value, externalSharingConfirmed: event.target.checked })
                }
              />
              I confirm that summaries and links from this task may be shared with people outside
              our company in this Slack Connect channel.
            </Box>
          )}
          {error && (
            <Text role="alert" fontSize="sm" color="status.error.fg">
              {error}
            </Text>
          )}
        </>
      )}
    </VStack>
  );
}
